// Startup capability check (§7). Everything the engine needs, checked once.

export type Capabilities = {
  ok: boolean;
  missing: string[];
  maxTextureSize: number;
  floatLinear: boolean; // OES_texture_float_linear (RGBA32F filtering); RGBA16F always filters in WebGL2
  renderer: string;
};

export function probeGl(gl: WebGL2RenderingContext | null): Omit<Capabilities, 'ok'> & { ok: boolean } {
  const missing: string[] = [];
  if (!gl) return { ok: false, missing: ['WebGL2'], maxTextureSize: 0, floatLinear: false, renderer: '' };
  const cbf = gl.getExtension('EXT_color_buffer_float') || gl.getExtension('EXT_color_buffer_half_float');
  if (!cbf) missing.push('EXT_color_buffer_float');
  const floatLinear = !!gl.getExtension('OES_texture_float_linear');
  const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
  if (maxTextureSize < 4096) missing.push(`MAX_TEXTURE_SIZE >= 4096 (has ${maxTextureSize})`);
  const dbg = gl.getExtension('WEBGL_debug_renderer_info');
  const renderer = dbg
    ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL))
    : String(gl.getParameter(gl.RENDERER));
  return { ok: missing.length === 0, missing, maxTextureSize, floatLinear, renderer };
}

/** Checks that a worker can create an OffscreenCanvas WebGL2 context (needed by the export renderer). */
function probeWorkerGl(): Promise<boolean> {
  return new Promise((resolve) => {
    try {
      const w = new Worker(new URL('./capabilities.worker.ts', import.meta.url), { type: 'module' });
      const t = setTimeout(() => {
        w.terminate();
        resolve(false);
      }, 5000);
      w.onmessage = (e: MessageEvent<boolean>) => {
        clearTimeout(t);
        w.terminate();
        resolve(e.data === true);
      };
      w.onerror = () => {
        clearTimeout(t);
        w.terminate();
        resolve(false);
      };
    } catch {
      resolve(false);
    }
  });
}

export async function checkCapabilities(): Promise<Capabilities> {
  const missing: string[] = [];
  if (typeof OffscreenCanvas === 'undefined') missing.push('OffscreenCanvas');
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl2');
  const probe = probeGl(gl);
  missing.push(...probe.missing);
  if (typeof OffscreenCanvas !== 'undefined' && !(await probeWorkerGl())) missing.push('WebGL2 in workers');
  gl?.getExtension('WEBGL_lose_context')?.loseContext();
  return { ...probe, ok: missing.length === 0, missing };
}
